import {
  select, call, put, takeLatest, takeLeading,
} from 'redux-saga/effects';
import history from '../../../history';
import {
  authInitRequest,
  authInitSuccess,
  authInitFail,
  logInFail,
  logInRequest,
  logInSuccess,
  logOut,
  refreshFail,
  refreshRequest,
  refreshSuccess,
  unauthorizedRequest,
} from './actions';
import AuthService from '../../../services/AuthService';
import { AccessBundle, TokenSet } from './types';
import { selectAuthTokens } from './selectors';
import apiService from '../../../services/ApiService';
import { getErrorNotification } from '../../util';
import { notify } from '../notifications/actions';
import { selectCurrentUser } from '../users/selectors';

function* initAuth() {
  const {
    access = null,
    refresh = null,
    expireDate = null,
  }: Partial<TokenSet> = JSON.parse(localStorage.getItem('auth-tokens') as string) || {};
  const authRemember = JSON.parse(localStorage.getItem('auth-remember') as string) || '';

  if (access === null) {
    return yield put(authInitFail());
  }

  yield put(
    authInitSuccess({
      access,
      refresh,
      expireDate,
      remember: authRemember ? authRemember.email : '',
    }),
  );
}

function updateRememberStore({
  payload: {
    rememberMe,
    email,
  },
}: ReturnType<typeof logInRequest>) {
  if (rememberMe) {
    localStorage.setItem('auth-remember', JSON.stringify({ email }));
  } else {
    localStorage.removeItem('auth-remember');
  }
}

function updateTokensStore({ payload }: ReturnType<typeof logInSuccess>) {
  localStorage.setItem('auth-tokens', JSON.stringify(payload));
}

function clearTokensStore() {
  apiService.setToken(null);
  localStorage.removeItem('auth-tokens');
}

function* attemptLogIn({ payload }: ReturnType<typeof logInRequest>) {
  try {
    const tokens: AccessBundle = yield call(AuthService.login, payload);
    yield put(
      logInSuccess({
        access: tokens.access_token,
        refresh: tokens.refresh_token,
        expireDate: Date.now() + tokens.expires_in * 1000,
      }),
    );
  } catch (e) {
    yield put(logInFail());
    yield put(notify(getErrorNotification(e)));
  }
}

function* attemptUnauthorized() {
  try {
    const tokens = JSON.parse(localStorage.getItem('auth-tokens') as string) || {};
    yield call(AuthService.unauthorized, tokens.refresh);
  } catch (e) {

  }
}

function* attemptRefresh() {
  try {
    const { refresh }: TokenSet = yield select(selectAuthTokens);

    if (refresh) {
      const tokens: AccessBundle = yield call(AuthService.refresh, refresh);
      return yield put(
        refreshSuccess({
          access: tokens.access_token,
          refresh: tokens.refresh_token,
          expireDate: Date.now() + tokens.expires_in * 1000,
        }),
      );
    }

    yield put(notify(
      {
        type: 'error',
        message: 'No refresh token found.',
      },
    ));
    yield put(refreshFail());
  } catch (e) {
    yield put(refreshFail());
    const { loading: currentUserLoading } = yield select(selectCurrentUser);
    if (!currentUserLoading) {
      yield put(notify(getErrorNotification(e)));
    }
    yield history.push('/pages/login');
  }

  return yield put(logOut());
}

export default [
  takeLatest(authInitRequest.type, initAuth),
  takeLatest(logInRequest.type, updateRememberStore),
  takeLatest(logInRequest.type, attemptLogIn),
  takeLatest(unauthorizedRequest.type, attemptUnauthorized),
  takeLatest([logInSuccess.type, refreshSuccess.type], updateTokensStore),
  takeLeading(refreshRequest.type, attemptRefresh),
  takeLatest(logOut.type, clearTokensStore),
  // takeLatest(authInitSuccess.type, attemptUnauthorized),
];
